import { Button } from './Button';
import { Icon } from './Icon';

/**
 * Loading, error and empty — written once, for every request in the app.
 *
 * A page passes the result of `useResource` and a render function. It never
 * writes `if (loading) return <Spinner/>`, which is how those three states end
 * up looking different on nine screens and missing entirely on the tenth.
 */

export function AsyncBoundary({
  state,
  children,
  skeleton,
  isEmpty,
  empty,
  className = '',
}) {
  const { data, error, loading, reload } = state;

  if (error) {
    return <ErrorState error={error} onRetry={reload} className={className} />;
  }

  // Only show the skeleton on the first load. A background refresh keeps the
  // current data on screen — a dashboard that blanks every 15 seconds is
  // unusable, however honest the spinner is.
  if (loading && !data) {
    return skeleton ?? <LoadingState className={className} />;
  }

  if (!data) return null;

  if (isEmpty && isEmpty(data)) {
    return empty ?? <EmptyState className={className} />;
  }

  return children(data);
}

export function LoadingState({ className = '', label = 'Loading' }) {
  return (
    <div className={`flex items-center justify-center gap-2 py-14 text-ink-3 ${className}`}>
      <span
        className="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin"
        aria-hidden="true"
      />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function ErrorState({ error, onRetry, className = '' }) {
  const message = error?.message || 'Something went wrong.';
  const isPermission = error?.status === 403 || error?.status === 404;

  return (
    <div className={`flex flex-col items-center gap-3 px-6 py-14 text-center ${className}`}>
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-full ${
          isPermission ? 'bg-warning-soft text-warning-ink' : 'bg-critical-soft text-critical-ink'
        }`}
      >
        <Icon name={isPermission ? 'shield' : 'critical'} size="lg" />
      </span>
      <div>
        <p className="text-sm font-medium text-ink">
          {isPermission ? 'You do not have access to this' : 'That did not load'}
        </p>
        <p className="mt-1 max-w-sm text-[13px] text-ink-3">{message}</p>
      </div>
      {onRetry && !isPermission && (
        <Button size="sm" icon="refresh" onClick={() => onRetry()}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function EmptyState({
  icon = 'search',
  title = 'Nothing here yet',
  description,
  action,
  className = '',
}) {
  return (
    <div className={`flex flex-col items-center gap-3 px-6 py-14 text-center ${className}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-2 text-ink-3">
        <Icon name={icon} size="lg" />
      </span>
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        {description && <p className="mt-1 max-w-sm text-[13px] text-ink-3">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** Shimmer placeholders, for the shapes a skeleton should keep. */
export function Skeleton({ className = '', style }) {
  return <div className={`animate-pulse rounded-md bg-surface-2 ${className}`} style={style} />;
}

export function SkeletonRows({ rows = 5, className = '' }) {
  return (
    <div className={`space-y-2 p-4 ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}
